(() => {
  class OPHeaderDDM {
    static selector = '[data-op-header-ddm]';

    static instances = new Map();

    static initAll(root = document) {
      OPHeaderDDM.getElements(root).forEach((element) => {
        if (!OPHeaderDDM.instances.has(element)) {
          OPHeaderDDM.instances.set(element, new OPHeaderDDM(element));
        }
      });
    }

    static destroyAll(root = document) {
      OPHeaderDDM.instances.forEach((instance, element) => {
        if (root === element || root.contains?.(element)) {
          instance.destroy();
          OPHeaderDDM.instances.delete(element);
        }
      });
    }

    static getElements(root) {
      const elements = [];

      if (root instanceof Element && root.matches(OPHeaderDDM.selector)) {
        elements.push(root);
      }

      if (typeof root.querySelectorAll === 'function') {
        root.querySelectorAll(OPHeaderDDM.selector).forEach((element) => {
          elements.push(element);
        });
      }

      return elements;
    }

    constructor(element) {
      this.element = element;
      this.panel = element.querySelector('.op-header-ddm__panel');
      this.drawer = element;
      this.drawerDetails = this.panel?.closest('details');
      this.drawerSummary = this.drawerDetails?.querySelector('summary');
      this.accordionItems = Array.from(element.querySelectorAll('.op-header-ddm__accordion-item'));
      this.accordionDetails = Array.from(element.querySelectorAll('.op-header-ddm__accordion-details'));
      this.mobileCloseTimers = new WeakMap();

      if (!this.panel || !this.drawerDetails) {
        return;
      }

      this.handleResize = this.syncMobileAccordionState.bind(this);
      this.handlePanelKeyUp = this.onPanelKeyUp.bind(this);
      this.handlePanelClick = this.onPanelClick.bind(this);
      this.handleAccordionToggle = this.onAccordionToggle.bind(this);
      this.handleDrawerToggle = this.onDrawerToggle.bind(this);

      this.bindEvents();
      this.syncMobileAccordionState();
    }

    bindEvents() {
      this.accordionDetails.forEach((details) => {
        details.removeAttribute('name');
        details.addEventListener('toggle', this.handleAccordionToggle);
      });

      this.drawerDetails.addEventListener('toggle', this.handleDrawerToggle);
      window.addEventListener('resize', this.handleResize);
      this.panel.addEventListener('keyup', this.handlePanelKeyUp, true);
      this.panel.addEventListener('click', this.handlePanelClick, true);
    }

    destroy() {
      this.accordionDetails.forEach((details) => {
        details.removeEventListener('toggle', this.handleAccordionToggle);
        this.clearMobileCloseTimer(details);
      });

      this.drawerDetails?.removeEventListener('toggle', this.handleDrawerToggle);
      window.removeEventListener('resize', this.handleResize);
      this.panel?.removeEventListener('keyup', this.handlePanelKeyUp, true);
      this.panel?.removeEventListener('click', this.handlePanelClick, true);
    }

    resetAccordions() {
      this.accordionDetails.forEach((details) => {
        this.clearMobileCloseTimer(details);
        details.removeAttribute('open');
        details.classList.remove('is-ddm-open', 'is-ddm-closing', 'is-mobile-open');
      });

      this.accordionItems.forEach((item) => item.classList.remove('is-mobile-open'));
      this.panel.classList.remove('is-mobile-submenu-open');
    }

    resetScrollPosition() {
      this.panel.scrollTop = 0;
    }

    syncHeaderMenuOpenState() {
      const header = this.element.closest('.section-header');

      if (header) {
        header.classList.toggle('menu-open', this.drawerDetails.open);
      }
    }

    onDrawerToggle() {
      this.syncHeaderMenuOpenState();

      if (this.drawerDetails.open) {
        this.resetScrollPosition();
        return;
      }

      this.resetAccordions();
      this.resetScrollPosition();
    }

    closeDrawer(event) {
      if (!this.drawerDetails?.hasAttribute('open')) {
        return;
      }

      if (typeof this.drawer.closeMenuDrawer === 'function' && this.drawerSummary) {
        this.drawer.closeMenuDrawer(event, this.drawerSummary);
      } else {
        this.drawerDetails.removeAttribute('open');
        this.drawerSummary?.setAttribute('aria-expanded', 'false');
        this.drawerDetails.classList.remove('menu-opening');
        document.body.classList.remove(`overflow-hidden-${this.drawer.dataset.breakpoint || 'desktop'}`);
      }

      this.resetAccordions();
      this.resetScrollPosition();
    }

    onAccordionToggle(event) {
      const details = event.currentTarget;

      if (details.open) {
        this.openAccordion(details);
        return;
      }

      this.finishAccordionClose(details);
    }

    openAccordion(details) {
      this.clearMobileCloseTimer(details);
      details.classList.remove('is-ddm-closing');
      details.classList.add('is-ddm-open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'true');

      this.accordionDetails.forEach((accordionDetails) => {
        if (accordionDetails !== details && accordionDetails.open) {
          this.closeAccordion(accordionDetails);
        }
      });

      this.syncMobileAccordionState();
    }

    closeAccordion(details) {
      if (!details.open) {
        this.finishAccordionClose(details);
        return;
      }

      this.clearMobileCloseTimer(details);
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');

      if (this.isMobileViewport()) {
        details.removeAttribute('open');
        this.finishAccordionClose(details);
        return;
      }

      details.classList.remove('is-ddm-open');
      details.classList.add('is-ddm-closing');

      const timer = window.setTimeout(() => {
        details.removeAttribute('open');
        this.finishAccordionClose(details);
      }, 240);

      this.mobileCloseTimers.set(details, timer);
    }

    finishAccordionClose(details) {
      const item = details.closest('.op-header-ddm__accordion-item');

      this.clearMobileCloseTimer(details);
      details.classList.remove('is-ddm-open', 'is-ddm-closing', 'is-mobile-open');
      item?.classList.remove('is-mobile-open');

      this.syncMobileAccordionState();
    }

    syncMobileAccordionState() {
      const isMobile = this.isMobileViewport();
      const openDetails = isMobile
        ? this.accordionDetails.find((details) => details.open && details.classList.contains('is-ddm-open')) ||
          this.accordionDetails.find((details) => details.open)
        : null;

      this.panel.classList.toggle('is-mobile-submenu-open', Boolean(openDetails));

      this.accordionItems.forEach((item) => item.classList.remove('is-mobile-open'));
      this.accordionDetails.forEach((details) => {
        details.classList.remove('is-mobile-open');
      });

      if (!openDetails) {
        return;
      }

      openDetails.classList.add('is-mobile-open');

      const openItem = openDetails.closest('.op-header-ddm__accordion-item');

      if (openItem) {
        openItem.classList.add('is-mobile-open');
      }
    }

    isMobileViewport() {
      return window.matchMedia('(max-width: 1023px)').matches;
    }

    clearMobileCloseTimer(details) {
      const timer = this.mobileCloseTimers.get(details);

      if (timer) {
        window.clearTimeout(timer);
        this.mobileCloseTimers.delete(details);
      }
    }

    onPanelKeyUp(event) {
      if (event.code.toUpperCase() !== 'ESCAPE' || !window.matchMedia('(min-width: 1024px)').matches) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.closeDrawer(event);
    }

    onPanelClick(event) {
      const closeButton = event.target.closest('.op-header-ddm__close');

      if (closeButton) {
        event.preventDefault();
        event.stopPropagation();
        this.closeDrawer(event);
        return;
      }

      const submenuLink = event.target.closest(
        '.op-header-ddm__accordion-child-link, .op-header-ddm__accordion-grandchild-link'
      );

      if (submenuLink) {
        this.closeDrawer(event);
        return;
      }

      if (!event.target.closest('[data-ddm-accordion-summary]')) {
        return;
      }

      event.stopPropagation();

      const details = event.target.closest('.op-header-ddm__accordion-details');

      if (!details?.open || this.isMobileViewport()) {
        return;
      }

      event.preventDefault();
      this.closeAccordion(details);
    }
  }

  const initialize = () => {
    OPHeaderDDM.initAll(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    OPHeaderDDM.initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    OPHeaderDDM.destroyAll(event.target);
  });
})();
